import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react"
import { nip19 } from "nostr-tools"
import { generateSecretKey, getPublicKey } from "nostr-tools/pure"
import { useBranding, useConfig } from "@/config/context"
import { withClientTag } from "@/nostr/api/feed"
import { buildProfileDraft } from "@/nostr/api/social"
import { LocalSigner } from "@/nostr/signers"
import { useAuth } from "@/state/AuthContext"
import { useNostr } from "@/state/NostrContext"
import { useRelays } from "@/state/RelayContext"
import { useToast } from "@/state/ToastContext"
import { Icon } from "./Icon"
import { Modal } from "./Modal"

export interface CreateAccountDialogProps {
  open: boolean
  onClose: () => void
  /** Goes back to the login options. */
  onBack: () => void
}

interface ProfileDraftFields {
  name: string
  displayName: string
  about: string
  picture: string
}

const EMPTY_PROFILE: ProfileDraftFields = { name: "", displayName: "", about: "", picture: "" }

/**
 * Two step account creation: generate and back up a key, then (optionally)
 * publish a kind 0 profile. The key is never stored by the application.
 */
export function CreateAccountDialog({ open, onClose, onBack }: CreateAccountDialogProps) {
  const branding = useBranding()
  const config = useConfig()
  const { loginWithSecretKey } = useAuth()
  const { pool } = useNostr()
  const { writeTargets } = useRelays()
  const { push } = useToast()

  const [step, setStep] = useState<1 | 2>(1)
  const [secretKey, setSecretKey] = useState<Uint8Array>(() => generateSecretKey())
  const [backedUp, setBackedUp] = useState(false)
  const [copied, setCopied] = useState(false)
  const [profile, setProfile] = useState<ProfileDraftFields>(EMPTY_PROFILE)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const errorRef = useRef<HTMLParagraphElement>(null)

  const nsec = useMemo(() => nip19.nsecEncode(secretKey), [secretKey])
  const npub = useMemo(() => nip19.npubEncode(getPublicKey(secretKey)), [secretKey])

  const regenerate = useCallback(() => {
    setSecretKey(generateSecretKey())
    setBackedUp(false)
    setCopied(false)
  }, [])

  useEffect(() => {
    if (!open) return
    setStep(1)
    setProfile(EMPTY_PROFILE)
    setError(undefined)
    setBusy(false)
    regenerate()
  }, [open, regenerate])

  const downloadBackup = () => {
    const content = [
      `${branding.name} — Nostr account backup`,
      `Created: ${new Date().toISOString()}`,
      "",
      `Public key (npub): ${npub}`,
      `Public key (hex):  ${getPublicKey(secretKey)}`,
      `Private key (nsec): ${nsec}`,
      "",
      "The nsec IS the account: anyone who has it can post as you.",
      "Store this file somewhere safe, never share it and never upload it.",
      "",
    ].join("\n")
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `nostr-account-${npub.slice(0, 12)}.txt`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 2000)
    push("success", "Backup file downloaded")
  }

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(nsec)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2500)
    } catch {
      push("error", "Could not copy — select the key and copy it manually")
    }
  }

  const finish = async () => {
    setBusy(true)
    setError(undefined)
    try {
      const signer = new LocalSigner(secretKey)
      const pubkey = await signer.getPublicKey()
      const fields = {
        name: profile.name || undefined,
        display_name: profile.displayName || undefined,
        about: profile.about || undefined,
        picture: profile.picture || undefined,
      }
      if (Object.values(fields).some(Boolean)) {
        try {
          const draft = buildProfileDraft(fields, {}, pubkey)
          const signed = await signer.signEvent({
            ...draft,
            tags: withClientTag(draft.tags, branding.name),
          })
          const relays = writeTargets.length > 0 ? writeTargets : config.relays.defaultRelays
          const summary = await pool.publish(signed, relays)
          if (summary.accepted > 0) push("success", "Profile published")
          else push("info", "Account created — your profile will publish once a relay answers")
        } catch {
          push("info", "Account created — the profile could not be published yet")
        }
      }
      await loginWithSecretKey(secretKey)
      push("success", "Your Nostr account is ready")
      onClose()
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : String(createError))
      requestAnimationFrame(() => errorRef.current?.focus())
    } finally {
      setBusy(false)
    }
  }

  const updateField =
    (field: keyof ProfileDraftFields) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setProfile((current) => ({ ...current, [field]: event.target.value }))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={step === 1 ? "Create Your Nostr Account" : "Add a profile"}
      description={
        step === 1
          ? "Generate your unique private key. This is your digital identity."
          : "Optional: tell people who you are. You can change this later in Settings."
      }
      size="small"
      className="wizard-dialog"
      footer={
        step === 1 ? (
          <div className="wizard-footer">
            <button type="button" className="btn btn-ghost" onClick={onBack}>
              Back
            </button>
            <button type="button" className="btn btn-primary" disabled={!backedUp} onClick={() => setStep(2)}>
              Continue
            </button>
          </div>
        ) : (
          <div className="wizard-footer">
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setStep(1)}>
              Back
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void finish()}>
              {busy ? "Creating…" : "Create account"}
            </button>
          </div>
        )
      }
    >
      <ol className="wizard-steps" aria-label={`Step ${step} of 2`}>
        <li className={`wizard-step ${step === 1 ? "active" : "done"}`} aria-current={step === 1 ? "step" : undefined}>
          1
        </li>
        <li className="wizard-line" aria-hidden="true" />
        <li className={`wizard-step ${step === 2 ? "active" : ""}`} aria-current={step === 2 ? "step" : undefined}>
          2
        </li>
      </ol>

      {step === 1 ? (
        <div className="wizard-body">
          <div className="wizard-warning" role="note">
            <strong>
              <Icon name="alert" size={16} /> Critical: Save Your Private Key
            </strong>
            <p>
              Your private key IS your account. There is no password recovery. If you lose it, you lose your
              account forever. Please save it in a secure location.
            </p>
          </div>

          <label className="field-label" htmlFor="new-account-nsec">
            Your Private Key
          </label>
          <div className="nsec-row">
            <input
              id="new-account-nsec"
              className="mono"
              readOnly
              spellCheck={false}
              value={nsec}
              onFocus={(event) => event.currentTarget.select()}
            />
            <button
              type="button"
              className="icon-button"
              title="Generate a different key"
              aria-label="Generate a different key"
              onClick={regenerate}
            >
              <Icon name="refresh" size={16} />
            </button>
          </div>

          <div className="wizard-actions-row">
            <button type="button" className="btn btn-primary" onClick={downloadBackup}>
              <Icon name="download" size={16} /> Download Backup File
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => void copyKey()}>
              <Icon name="copy" size={16} /> {copied ? "Copied" : "Copy to Clipboard"}
            </button>
          </div>

          <label className="wizard-check">
            <input type="checkbox" checked={backedUp} onChange={(event) => setBackedUp(event.target.checked)} />
            <span>I have safely backed up my private key</span>
          </label>

          <p className="muted small">
            Public key: <span className="mono">{npub}</span>
          </p>
        </div>
      ) : (
        <div className="wizard-body">
          <div className="field-grid">
            <div>
              <label className="field-label" htmlFor="new-account-name">
                Name
              </label>
              <input
                id="new-account-name"
                value={profile.name}
                onChange={updateField("name")}
                placeholder="satoshi"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="new-account-display-name">
                Display name
              </label>
              <input
                id="new-account-display-name"
                value={profile.displayName}
                onChange={updateField("displayName")}
                placeholder="Satoshi Nakamoto"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="new-account-about">
                About
              </label>
              <textarea
                id="new-account-about"
                rows={3}
                value={profile.about}
                onChange={updateField("about")}
                placeholder="A few words about you"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="new-account-picture">
                Profile picture URL
              </label>
              <input
                id="new-account-picture"
                value={profile.picture}
                onChange={updateField("picture")}
                placeholder="https://…"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>
          <p className="muted small">
            Identity: <span className="mono">{npub}</span>
          </p>
        </div>
      )}

      {error && (
        <p className="form-error" role="alert" tabIndex={-1} ref={errorRef}>
          {error}
        </p>
      )}
    </Modal>
  )
}
